import React, { useCallback, useEffect, useState } from "react";
import {
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import useDragScroll from "../../../hooks/useDragScroll";
import useLongPress from "../../../hooks/useLongPress";
import { restoreAllFilters, selectChipFilters } from "../utils/leadFilters";
import { useLeadListsQuery } from "../hooks/useLeadsQuery";
import LeadsDeleteListDialog from "./Leads.DeleteListDialog";

// Fileira de chips estilo WhatsApp logo abaixo da busca: Todas / Favoritas /
// uma por lista do colaborador / "+". Escolher qualquer chip que não seja
// "Todas" zera os demais filtros; voltar para "Todas" os restaura. A busca
// vive em outro estado e é mantida.
// Como no WhatsApp, segurar o chip de uma lista (ou clique direito no
// desktop) abre o menu Gerenciar / Excluir.
const LeadsListChips = ({ filters, onChange, onCreate, onEdit }) => {
  const { t } = useTranslation();
  const { data: lists = [], isSuccess, isFetching } = useLeadListsQuery();
  const [menu, setMenu] = useState(null);
  const [listToDelete, setListToDelete] = useState(null);

  const selected = filters.list || (filters.isFavorite ? "favorites" : "all");
  const selectedList = lists.find((l) => l.uuid === filters.list);

  // Lista selecionada que não existe mais (apagada aqui ou em outro aparelho) → "Todas".
  useEffect(() => {
    if (isSuccess && !isFetching && filters.list && !selectedList) {
      onChange(restoreAllFilters(filters));
    }
  }, [isSuccess, isFetching, filters, selectedList, onChange]);

  const openMenu = useCallback((anchor, list) => setMenu({ anchor, list }), []);
  const longPress = useLongPress(openMenu);
  const dragScroll = useDragScroll();

  const chipProps = (key) => ({
    variant: selected === key ? "filled" : "outlined",
    // Selecionado no tom suave do primary (primarySoft), com o texto que o tema calcula.
    color: selected === key ? "primarySoft" : "default",
  });

  const closeMenu = () => setMenu(null);

  return (
    <Stack
      direction="row"
      spacing={1}
      {...dragScroll}
      // Fileira maior que a tela rola de lado: no toque, arrastando o dedo; no
      // mouse, arrastando (useDragScroll). Sem barra de rolagem, como no WhatsApp.
      sx={{
        overflowX: "auto",
        userSelect: "none",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        "& > *": { flexShrink: 0 },
      }}
    >
      <Chip
        {...chipProps("all")}
        label={t("leads.lists.all")}
        onClick={() => onChange(restoreAllFilters(filters))}
      />
      <Chip
        {...chipProps("favorites")}
        label={t("leads.lists.favorites")}
        onClick={() =>
          onChange(selectChipFilters(filters, { isFavorite: true }))
        }
      />
      {lists.map((list) => (
        <Chip
          key={list.uuid}
          {...chipProps(list.uuid)}
          label={list.name}
          onClick={() =>
            onChange(selectChipFilters(filters, { list: list.uuid }))
          }
          {...longPress(list)}
          // Sem seleção de texto nem menu nativo do iOS ao segurar o chip.
          sx={{ userSelect: "none", WebkitTouchCallout: "none" }}
        />
      ))}
      <Chip
        variant="outlined"
        aria-label={t("leads.lists.create")}
        size="medium"
        label={<AddIcon fontSize="medium" sx={{ verticalAlign: "middle" }} />}
        onClick={onCreate}
      />

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu();
            onEdit(menu.list);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("leads.lists.manage")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setListToDelete(menu.list);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { color: "error" } }}>
            {t("leads.lists.delete")}
          </ListItemText>
        </MenuItem>
      </Menu>

      {listToDelete && (
        <LeadsDeleteListDialog list={listToDelete} onClose={() => setListToDelete(null)} />
      )}
    </Stack>
  );
};

export default LeadsListChips;
